import { useId } from "react";

import { Stack } from "@mui/material";
import { Control, FieldValues, Path, PathValue, useController } from "react-hook-form";

import { useTranslate } from "@/common/hooks/useTranslate";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";
import { FormTypography } from "@/forms-design-system/FormTypography";

interface SepaMandateConsentProps<T extends FieldValues> {
  control: Control<T>;
  organizationName: string;
}

export function SepaMandateConsent<T extends FieldValues>({ control, organizationName }: SepaMandateConsentProps<T>) {
  const { t } = useTranslate();
  const errorId = useId();
  const { field, fieldState } = useController({
    control,
    name: "sepaMandateAccepted" as Path<T>,
    defaultValue: false as PathValue<T, Path<T>>,
    shouldUnregister: true,
    rules: { validate: (value) => value === true || t("common", "payment.sepa.consentRequired") },
  });

  return (
    <Stack gap={0.5}>
      <FormCheckbox
        name={`${field.name}-${errorId}`}
        checked={field.value === true}
        required
        label={t("common", "payment.sepa.consent", { organizationName })}
        onChange={(_, checked) => field.onChange(checked)}
        onBlur={field.onBlur}
        inputRef={(input) => {
          field.ref(input);
          if (input) {
            input.required = true;
            input.setAttribute("aria-invalid", String(Boolean(fieldState.error)));
            if (fieldState.error) {
              input.setAttribute("aria-describedby", errorId);
            } else {
              input.removeAttribute("aria-describedby");
            }
          }
        }}
        error={Boolean(fieldState.error)}
        data-test="sepa-mandate-consent"
      />
      {fieldState.error && (
        <FormTypography id={errorId} role="alert" variant="caption" vibe="error">
          {fieldState.error.message}
        </FormTypography>
      )}
    </Stack>
  );
}
